/**
 * Page-context grounding for Agapella (page-context epic).
 *
 * Agapella is reachable from any screen — the widget floats over all of them —
 * but the message text rarely says *where* the user is («а кто это?» / «мне
 * подойдёт?»). This module tracks the current screen and exposes it as a
 * `page_context` hint; the send path attaches it to every companion message as
 * `message.page_context`. The backend stores it on the message and hands it to
 * the AI as grounding — a hint, never a claim the user did anything.
 *
 * What travels: `user_id`, the current `route`, the `page_type`, and the open
 * entity (`entity_type` / `entity_id`) when the screen has one. `user_id` is
 * filled in at send time, where the authenticated id is known for certain.
 *
 * Tracking rule (fed by the navigation container's `onStateChange`):
 *  - every screen produces a context, so no message ever goes out without one;
 *  - an entity screen (master / practice / event / live / circle / profile)
 *    also carries which entity is open;
 *  - the chat screen keeps the entity the user came from — that's the grounding
 *    they're asking about — while its own route/page_type take over.
 *
 * Adding a screen: add a line to PAGE_TYPES (and ENTITIES if it opens an
 * entity). Unlisted screens still emit a context — the page type falls back to
 * a kebab-cased route name — so a new screen is never silently context-less.
 */

export interface PageContext {
  /** Authenticated user the message is sent as. Filled in by the send path. */
  user_id?: string;
  route?: string;
  page_type?: string;
  entity_type?: string;
  entity_id?: string;
  entity_title?: string;
  /**
   * What the user has DONE to this page — the filter they applied, the tab they
   * opened. Distinct from the entity: on a catalogue there is nothing open, but
   * «отфильтровал Сон» is the strongest statement of intent there is, and it was
   * being thrown away.
   */
  page_state?: string;
}

type Route = { name?: string; params?: Record<string, unknown> } | undefined;

/** Route name → page type. Unlisted routes fall back to a kebab-cased name. */
const PAGE_TYPES: Record<string, string> = {
  Home: 'home',
  Landing: 'landing',
  Masters: 'masters',
  MasterDetail: 'master',
  Practices: 'practices',
  PracticeDetail: 'practice',
  EventsCalendar: 'events',
  EventsAll: 'events',
  EventDetail: 'event',
  Lives: 'lives',
  LiveDetail: 'live',
  RoomAnnouncement: 'room',
  // The user is inside the broadcast right now — a different situation from the
  // announcement page, so it gets its own page type rather than folding into
  // `room`. The pre-flight (green room) is the moment just before it.
  LiveRoomSession: 'live-session',
  LiveSessionEntry: 'live-session-entry',
  PracticeRooms: 'rooms',
  Community: 'community',
  CircleChat: 'circle',
  Dashboard: 'profile',
  Me: 'profile',
  PublicUserProfile: 'profile',
  Settings: 'settings',
  Chats: 'chats',
  MyCalendar: 'calendar',
  Schedule: 'schedule',
  MyCollection: 'collection',
  AgapeCenter: 'agape-center',
  Feed: 'feed',
};

/**
 * Route name → the entity that screen opens, and the param holding its id.
 * `path` overrides the entity-type prefix when the screen lives at its own URL.
 */
const ENTITIES: Record<string, { type: string; param: string; path?: string }> = {
  MasterDetail: { type: 'master', param: 'masterId' },
  // CORE-502 — the param is the invite code for a shared link (/room/7JCiwi)
  // and the numeric id when opened from inside the app. Either identifies the
  // session; the assistant only has to know which one is open.
  RoomAnnouncement: { type: 'room', param: 'roomId' },
  // Same room entity, reached at its own two URLs — the live broadcast and the
  // pre-flight before it.
  LiveRoomSession: { type: 'room', param: 'roomId', path: '/practice-rooms/session' },
  LiveSessionEntry: { type: 'room', param: 'roomId', path: '/live-session/join' },
  PracticeDetail: { type: 'practice', param: 'id' },
  EventDetail: { type: 'event', param: 'id' },
  LiveDetail: { type: 'live', param: 'id' },
  CircleChat: { type: 'circle', param: 'circleId' },
  PublicUserProfile: { type: 'profile', param: 'userId' },
};

/** Deep-link params arrive as strings (`?isHost=false`), so `false` is truthy. */
function isTrue(v: unknown): boolean {
  return v === true || v === 'true';
}

/** Who leads the live session, what runs in it, and the user's role in it. */
function liveSessionState(p: Record<string, unknown>): string | undefined {
  const parts: string[] = [];
  if (typeof p.masterName === 'string' && p.masterName) parts.push(`ведущий — ${p.masterName}`);
  if (typeof p.practiceName === 'string' && p.practiceName) {
    parts.push(`практика «${p.practiceName}»`);
  }
  if (isTrue(p.isHost)) parts.push('пользователь ведёт Live-сессию');
  else if (isTrue(p.isSpectator)) parts.push('пользователь смотрит как зритель');
  else parts.push('пользователь — участник');
  return parts.join(', ');
}

/**
 * Route name → a short description of the page's own state, from its params.
 * Free context: it is already in the URL, no screen has to report it.
 */
const PAGE_STATE: Record<string, (p: Record<string, unknown>) => string | undefined> = {
  // Everything the live screen knows about the session is already in its params
  // — who leads it, which practice runs, which side of it the user is on. It is
  // exactly what «а кто ведёт?» asks for, so it must not be dropped.
  LiveRoomSession: liveSessionState,
  LiveSessionEntry: liveSessionState,
  Practices: (p) => {
    const parts: string[] = [];
    const dir = (p.category ?? p.preselectCategory) as string | undefined;
    if (dir) parts.push(`направление «${dir}»`);
    if (p.filter === 'favorites') parts.push('только избранное');
    return parts.length ? parts.join(', ') : undefined;
  },
  PublicUserProfile: (p) => (p.tab ? `вкладка «${p.tab}»` : undefined),
  Dashboard: (p) => (p.initialTab ? `вкладка «${p.initialTab}»` : undefined),
};

/** Entity type → URL prefix, so `route` reads like the address bar. */
const ENTITY_PATHS: Record<string, string> = {
  master: '/masters',
  practice: '/practices',
  event: '/events',
  live: '/lives',
  room: '/room',
  circle: '/circles',
  profile: '/u',
};

/** Screens hosting the companion chat — they must not erase the entity the
 *  user came from, which is what the question is usually about. */
const CHAT_ROUTES = new Set(['Chats']);

let current: PageContext | undefined;

/** `MasterDetail` → `master-detail`; keeps unlisted screens self-describing. */
function kebab(routeName: string): string {
  return routeName
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

function pathFor(routeName: string, entityId?: string): string {
  const entity = ENTITIES[routeName];
  if (entity && entityId) {
    const prefix = entity.path ?? ENTITY_PATHS[entity.type] ?? `/${entity.type}s`;
    return `${prefix}/${entityId}`;
  }
  if (routeName === 'Home') return '/';
  return `/${kebab(routeName)}`;
}

/** Map a navigation route to a PageContext. Every named route yields one. */
export function pageContextFromRoute(route: Route): PageContext | undefined {
  const name = route?.name;
  if (!name) return undefined;

  const params = route?.params ?? {};
  const entity = ENTITIES[name];
  const rawId = entity ? params[entity.param] : undefined;
  const entityId =
    rawId === undefined || rawId === null || rawId === '' ? undefined : String(rawId);

  const ctx: PageContext = {
    route: pathFor(name, entityId),
    page_type: PAGE_TYPES[name] ?? kebab(name),
  };
  // An entity screen reached without its id (deep-link mid-resolution) still
  // reports the page — just without claiming which entity is open.
  if (entity && entityId) {
    ctx.entity_type = entity.type;
    ctx.entity_id = entityId;
  }
  // A circle and a live session carry their own name in the route, so they need
  // no screen support.
  if (name === 'CircleChat' && typeof params.circleName === 'string' && params.circleName) {
    ctx.entity_title = params.circleName;
  }
  if (
    (name === 'LiveRoomSession' || name === 'LiveSessionEntry')
    && typeof params.roomTitle === 'string' && params.roomTitle
  ) {
    ctx.entity_title = params.roomTitle;
  }
  const state = PAGE_STATE[name]?.(params);
  if (state) ctx.page_state = state;
  return ctx;
}

/** Feed the current navigation route (call from the container's onStateChange). */
export function noteRoute(route: Route): void {
  const next = pageContextFromRoute(route);
  if (!next) return; // unnamed route mid-transition — keep what we had

  if (route?.name && CHAT_ROUTES.has(route.name) && current?.entity_type) {
    // Opening the chat: the page is now the chat, but the entity they came from
    // is the thing they're asking about — carry it along.
    current = {
      ...next,
      entity_type: current.entity_type,
      entity_id: current.entity_id,
      entity_title: current.entity_title,
    };
    return;
  }
  current = next;
}

/**
 * Let a screen fill in what only it knows once its data has loaded.
 *
 * The route alone can be too thin to act on: `/u/<userId>` says "a profile is
 * open" but carries a bare GUID, so Agapella could name neither the person nor
 * look them up — her master tools take a master-profile id or a NAME. The screen
 * has both by the time it renders, so it patches them in here.
 *
 * `matchEntityId` is the id the caller believes is open (the route's). The patch
 * is dropped when it no longer matches, so a slow fetch resolving after the user
 * has navigated away cannot describe the previous page as the current one.
 */
export function setPageEntity(
  matchEntityId: string,
  patch: {
    page_type?: string;
    entity_type?: string;
    entity_id?: string;
    entity_title?: string;
    page_state?: string;
  },
): void {
  if (!current || !matchEntityId || current.entity_id !== matchEntityId) return;
  current = { ...current, ...patch };
}

/** The page the user is writing from. */
export function getCurrentPageContext(): PageContext | undefined {
  return current;
}

/** Test seam — drop the tracked page between cases. */
export function __resetPageContext(): void {
  current = undefined;
}
